/* eslint-disable no-param-reassign */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MdOutlineWidgets } from 'react-icons/md';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { FormHandles } from '@unform/core';
import { AxiosResponse } from 'axios';

import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';

import { Container } from './styles';
import Modality, { ICategoryData } from './Modality';
import { IModalityData } from '../Modalities';
import Loading from '~/components/Loading';
import { IBatchCategory } from '../Interfaces';
import { IBatch } from '../Batches';

interface ICategories {
  typeSelected?: number;
  sold?: boolean;
  modalities: IModalityData[];
  onChange(
    data: IModalityData[],
    batchesCategories: IBatchCategory[],
    batches: IBatch[] | undefined
  ): void;
  event_id: number;
  className?: string;
  batchesCategories: IBatchCategory[];
  batches?: IBatch[];
  progressNumber?: number;
}

export interface ICategoriesHandle {
  submit: () => void;
}

const Categories: ForwardRefRenderFunction<ICategoriesHandle, ICategories> = (
  {
    typeSelected,
    sold,
    onChange,
    modalities: initialModalities,
    event_id,
    className,
    batchesCategories,
    batches,
    progressNumber,
  },
  ref
) => {
  const formRef = useRef<FormHandles>(null);
  const [modalities, setModalities] = useState<IModalityData[]>([]);
  const [oldModalities, setOldModalities] = useState<IModalityData[]>([]);
  const [categoriesError, setCategoriesError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Carregando...');

  useEffect(() => {
    if (progressNumber && progressNumber === 6) {
      api.delete(`batches-categories/events/${event_id}`).catch((err) => {
        console.log(err);
      });
    }
  }, [event_id, progressNumber]);

  useEffect(() => {
    if (initialModalities.length > 0) {
      setModalities(initialModalities);
      setOldModalities(JSON.parse(JSON.stringify(initialModalities)));
    }
  }, [initialModalities]);

  const handleChangeModality = useCallback(
    (categories: ICategoryData[], modalityIndex: number) => {
      const newModalities = modalities.slice();
      newModalities[modalityIndex].categories = categories;
    },
    [modalities]
  );

  // Verifica se é um ID temporário gerado com new Date().getTime()
  const isTemporaryId = (id: number): boolean => {
    return id.toString().length === 13;
  };

  const handleSubmit = useCallback(
    async (data) => {
      if (loading) return;
      if (
        modalities
          .map((modality) => modality.categories)
          .some((categories) => !categories || categories.length === 0)
      ) {
        Swal.fire(
          'Oops...',
          'Favor preencher todas as modalidades com ao menos uma categoria!',
          'error'
        );
        return;
      }
      try {
        setLoading(true);
        setLoadingText('Salvando...');
        formRef.current?.setErrors({});
        setCategoriesError('');

        const schema = Yup.object().shape({
          categories: Yup.string().test(
            'checkCategories',
            'Favor preencher todas as categorias',
            async () => {
              return modalities.every((modality) => {
                return (
                  modality.categories &&
                  modality.categories.every((category) => {
                    return !!category.name;
                  })
                );
              });
            }
          ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const newModalities = JSON.parse(
          JSON.stringify(modalities)
        ) as IModalityData[];

        const newBatchesCategories = JSON.parse(
          JSON.stringify(batchesCategories)
        ) as IBatchCategory[];

        let newBatches: IBatch[] | undefined;

        // Só processa batches se existir
        if (batches && batches.length > 0) {
          newBatches = JSON.parse(JSON.stringify(batches)) as IBatch[];

          // Converte as datas de volta para Date objects
          newBatches.forEach((batch) => {
            if (batch.expiration) {
              batch.expiration = new Date(batch.expiration);
            }
          });
        }

        if (newModalities.length > 0) {
          let modalityIndex = 0;
          for (const modality of newModalities) {
            const deletedCategories = oldModalities[
              modalityIndex
            ]?.categories?.filter(
              (oldCategory) =>
                !modality.categories?.some(
                  (category) => category.input_id === oldCategory.input_id
                )
            );

            if (modality.categories && modality.categories.length > 0) {
              let categoryIndex = 0;
              for (const category of modality.categories) {
                if (!category.id) {
                  const formDataCategory = {
                    type_id: typeSelected,
                    name: category.name,
                    is_elderly: category.elderly,
                    is_child: category.child,
                    is_male: category.male,
                    is_female: category.female,
                    min_quantity: category.qty_min || 0,
                    order: categoryIndex + 1,
                    has_kit: category.has_kit,
                    has_age_group: category.has_age_group,
                    min_age: category.min_age,
                    max_age: category.max_age,
                  };
                  const responseCategory = await api.post(
                    'categories',
                    formDataCategory
                  );

                  category.id = responseCategory.data.id;
                } else {
                  const formDataCategory = {
                    type_id: typeSelected,
                    name: category.name,
                    is_elderly: category.elderly,
                    is_child: category.child,
                    is_male: category.male,
                    is_female: category.female,
                    min_quantity: category.qty_min || 0,
                    order: categoryIndex + 1,
                    has_kit: category.has_kit,
                    has_age_group: category.has_age_group,
                    min_age: category.min_age,
                    max_age: category.max_age,
                  };
                  await api.put(`categories/${category.id}`, formDataCategory);
                }

                const isOldCategory = oldModalities[
                  modalityIndex
                ]?.categories?.some(
                  (oldCategory) =>
                    oldCategory.input_id === category.input_id &&
                    !isTemporaryId(oldCategory.input_id)
                );

                if (!isOldCategory) {
                  if (batches && batches.length > 0) {
                    for (const batch of batches) {
                      const batchCategoryFormData = {
                        event_id,
                        batch_id: batch.input_id,
                        category_id: category.id,
                        modality_id: Number(modality.id),
                      };

                      let responseBatchCategory: AxiosResponse;
                      const emptyBatchCategory = newBatchesCategories.find(
                        (batchCategory) =>
                          batchCategory.modality_id === modality.id &&
                          !batchCategory.category_id &&
                          !isTemporaryId(batchCategory.id)
                      );

                      if (emptyBatchCategory) {
                        responseBatchCategory = await api.put(
                          `batches-categories/${emptyBatchCategory.id}`,
                          batchCategoryFormData
                        );
                        const batchCategoryIndex =
                          newBatchesCategories.findIndex(
                            (batchCategory) =>
                              batchCategory.id === responseBatchCategory.data.id
                          );
                        if (batchCategoryIndex >= 0) {
                          newBatchesCategories[batchCategoryIndex].category_id =
                            responseBatchCategory.data.category_id;
                          newBatchesCategories[batchCategoryIndex].batch_id =
                            responseBatchCategory.data.batch_id;
                        }
                      } else {
                        responseBatchCategory = await api.post(
                          'batches-categories',
                          batchCategoryFormData
                        );
                        newBatchesCategories.push({
                          id: responseBatchCategory.data.id,
                          event_id: responseBatchCategory.data.event_id,
                          modality_id: responseBatchCategory.data.modality_id,
                          category_id: responseBatchCategory.data.category_id,
                          batch_id: responseBatchCategory.data.batch_id,
                          price: responseBatchCategory.data.price,
                          quantity: responseBatchCategory.data.quantity,
                          quantity_sold:
                            responseBatchCategory.data.quantity_sold,
                          new: true,
                        });
                      }

                      const newCategories =
                        newModalities[modalityIndex].categories;

                      if (newCategories) {
                        newCategories[categoryIndex].input_id =
                          responseBatchCategory.data.id;
                        newCategories[categoryIndex].id = category.id;
                      }

                      newModalities[modalityIndex].categories = newCategories;
                    }
                  } else {
                    const batchCategoryFormData = {
                      event_id,
                      category_id: category.id,
                      modality_id: Number(modality.id),
                      has_kit: category.has_kit,
                      has_age_group: category.has_age_group,
                      min_age: category.min_age,
                      max_age: category.max_age,
                    };

                    let responseBatchCategory: AxiosResponse;

                    const emptyBatchCategory = newBatchesCategories.find(
                      (batchCategory) =>
                        batchCategory.modality_id === modality.id &&
                        !batchCategory.category_id &&
                        !isTemporaryId(batchCategory.id)
                    );

                    if (emptyBatchCategory) {
                      responseBatchCategory = await api.put(
                        `batches-categories/${emptyBatchCategory.id}`,
                        batchCategoryFormData
                      );
                      const batchCategoryIndex = newBatchesCategories.findIndex(
                        (batchCategory) =>
                          batchCategory.id === responseBatchCategory.data.id
                      );
                      if (batchCategoryIndex >= 0) {
                        newBatchesCategories[batchCategoryIndex].category_id =
                          responseBatchCategory.data.category_id;
                        newBatchesCategories[batchCategoryIndex].batch_id =
                          responseBatchCategory.data.batch_id;
                      }
                    } else {
                      responseBatchCategory = await api.post(
                        'batches-categories',
                        batchCategoryFormData
                      );
                      newBatchesCategories.push({
                        id: responseBatchCategory.data.id,
                        event_id: responseBatchCategory.data.event_id,
                        modality_id: responseBatchCategory.data.modality_id,
                        category_id: responseBatchCategory.data.category_id,
                        batch_id: responseBatchCategory.data.batch_id,
                        price: responseBatchCategory.data.price,
                        quantity: responseBatchCategory.data.quantity,
                        quantity_sold: responseBatchCategory.data.quantity_sold,
                        new: true,
                      });
                    }

                    category.input_id = responseBatchCategory.data.id;
                  }
                } else if (
                  category.input_id &&
                  !isTemporaryId(category.input_id)
                ) {
                  const batchCategoryFormData = {
                    event_id,
                    category_id: category.id,
                    modality_id: Number(modality.id),
                    has_kit: category.has_kit,
                    has_age_group: category.has_age_group,
                    min_age: category.min_age,
                    max_age: category.max_age,
                  };

                  // Uma categoria possui um batches-categories por lote.
                  // "input_id" guarda apenas um registro "representante", então
                  // é preciso replicar o update para todos os lotes dessa
                  // modalidade+categoria, senão só o lote representante é atualizado.
                  const relatedBatchesCategories = newBatchesCategories.filter(
                    (batchCategory) =>
                      batchCategory.modality_id === Number(modality.id) &&
                      batchCategory.category_id === category.id
                  );

                  const batchesCategoriesToUpdate =
                    relatedBatchesCategories.length > 0
                      ? relatedBatchesCategories
                      : newBatchesCategories.filter(
                          (batchCategory) =>
                            batchCategory.id === category.input_id
                        );

                  for (const batchCategoryToUpdate of batchesCategoriesToUpdate) {
                    const responseBatchCategory = await api.put(
                      `batches-categories/${batchCategoryToUpdate.id}`,
                      batchCategoryFormData
                    );
                    const batchCategoryIndex = newBatchesCategories.findIndex(
                      (batchCategory) =>
                        batchCategory.id === responseBatchCategory.data.id
                    );
                    if (batchCategoryIndex >= 0) {
                      newBatchesCategories[batchCategoryIndex].category_id =
                        responseBatchCategory.data.category_id;
                    }
                  }

                  const newCategories = newModalities[modalityIndex].categories;
                  if (newCategories) {
                    newCategories[categoryIndex].id = category.id;
                  }

                  newModalities[modalityIndex].categories = newCategories;
                }

                categoryIndex += 1;
              }
            }

            if (deletedCategories && deletedCategories.length > 0) {
              for (const deletedCategory of deletedCategories) {
                if (batches && batches.length > 0) {
                  let batchIndex = 0;
                  for (const batch of batches) {
                    const deletedBatchesCategories =
                      batch.batchesCategories?.filter(
                        (batchCategory) =>
                          batchCategory.category_id === deletedCategory.id
                      );
                    for (const deletedBatchesCategory of deletedBatchesCategories ||
                      []) {
                      await api.delete(
                        `batches-categories/${deletedBatchesCategory.id}`
                      );

                      if (newBatches) {
                        const batchCategoryIndex = newBatches[
                          batchIndex
                        ].batchesCategories?.findIndex(
                          (batchCategory) =>
                            batchCategory.id === deletedBatchesCategory.id
                        );

                        if (
                          typeof batchCategoryIndex !== 'undefined' &&
                          batchCategoryIndex >= 0
                        ) {
                          newBatches[batchIndex].batchesCategories?.splice(
                            batchCategoryIndex,
                            1
                          );
                        }
                      }
                    }

                    batchIndex += 1;
                  }
                }
                await api.delete(`categories/${deletedCategory.id}`);
              }
            }

            modalityIndex += 1;
          }
        }

        try {
          await api.delete(`batches-categories/events/${event_id}`);
        } catch (error) {
          console.log(error);
        }

        console.log(newModalities);

        setModalities(newModalities);
        setOldModalities(JSON.parse(JSON.stringify(newModalities)));
        onChange(newModalities, newBatchesCategories, newBatches);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
          if (errors.categories) {
            setCategoriesError(errors.categories);
          }
        } else {
          Swal.fire('Oops...', 'Erro ao salvar as categorias!');
        }
      } finally {
        setLoading(false);
        setLoadingText('Carregando...');
      }
    },
    [
      loading,
      modalities,
      batchesCategories,
      batches,
      onChange,
      oldModalities,
      typeSelected,
      event_id,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        formRef.current?.submitForm();
      },
    }),
    []
  );

  return (
    <Container
      ref={formRef}
      onSubmit={handleSubmit}
      className={`${className} row`}
    >
      <div className="col-12">
        <h2 className="h4 fw-semibold text-center mt-5 mb-4">6 - Categorias</h2>
        <p className="mb-4">
          Cadastre ao menos uma categoria para cada modalidade do seu evento
          para seguir para o próximo passo.
        </p>
      </div>
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="h5 fw-normal roboto d-flex align-items-center">
            <MdOutlineWidgets color="#000" size={20} className="me-1" />
            Categorias
          </h3>
          {/* <span>
            <IoMdInformationCircleOutline size={16} color="#6C6C6C" />
          </span> */}
        </div>
        {modalities.map((modality, index) => (
          <Modality
            key={modality.input_id}
            data={modality}
            sold={sold}
            onChange={(categories) => handleChangeModality(categories, index)}
          />
        ))}
      </div>
      {categoriesError && (
        <span className="small text-danger error">{categoriesError}</span>
      )}
      <Loading active={loading} text={loadingText} />
    </Container>
  );
};

export default forwardRef(Categories);
